// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface WeightSample {
  id: string;
  flockId: string;
  flockNumber: string;
  flockName: string;
  farm: string;
  house: string;
  breed: string;
  birdType: string;
  recordDate: string;
  ageDays: number;
  ageWeeks: number;
  numberSampled: number;
  individualWeights: number[]; // kg
  avgWeightKg: number;
  targetWeightKg: number;
  minWeightKg: number;
  maxWeightKg: number;
  stdDeviation: number;
  uniformityPct: number; // % birds within ±10% of avg
  avgDailyGainG: number; // grams/day vs previous record
  weeklyGainG: number;   // grams/week
  fcr: number;
  feedConsumedKg: number;
  cumulativeFeedKg: number;
  recordedBy: string;
  notes: string;
}

export interface GrowthCurvePoint {
  ageDays: number;
  ageWeeks: number;
  targetWeightKg: number;
  actualWeightKg?: number;
  flockId?: string;
}

export interface FlockGrowthSummary {
  flockId: string;
  flockNumber: string;
  flockName: string;
  breed: string;
  birdType: string;
  farm: string;
  house: string;
  currentAgeDays: number;
  currentAvgWeightKg: number;
  targetWeightKg: number;
  weightVariancePct: number; // (actual - target) / target * 100
  latestUniformityPct: number;
  latestFcr: number;
  latestAdg: number; // avg daily gain grams
  totalSamples: number;
  status: 'on_track' | 'below_target' | 'above_target';
}

// ─── Target Weight Standards (Cobb 500 Broiler) ──────────────────────────────
export const COBB500_TARGETS: GrowthCurvePoint[] = [
  { ageDays: 0,  ageWeeks: 0,  targetWeightKg: 0.042 },
  { ageDays: 7,  ageWeeks: 1,  targetWeightKg: 0.170 },
  { ageDays: 14, ageWeeks: 2,  targetWeightKg: 0.430 },
  { ageDays: 21, ageWeeks: 3,  targetWeightKg: 0.820 },
  { ageDays: 28, ageWeeks: 4,  targetWeightKg: 1.310 },
  { ageDays: 35, ageWeeks: 5,  targetWeightKg: 1.870 },
  { ageDays: 42, ageWeeks: 6,  targetWeightKg: 2.420 },
  { ageDays: 49, ageWeeks: 7,  targetWeightKg: 2.940 },
  { ageDays: 56, ageWeeks: 8,  targetWeightKg: 3.420 },
];

export const ROSS308_TARGETS: GrowthCurvePoint[] = [
  { ageDays: 0,  ageWeeks: 0,  targetWeightKg: 0.042 },
  { ageDays: 7,  ageWeeks: 1,  targetWeightKg: 0.185 },
  { ageDays: 14, ageWeeks: 2,  targetWeightKg: 0.460 },
  { ageDays: 21, ageWeeks: 3,  targetWeightKg: 0.870 },
  { ageDays: 28, ageWeeks: 4,  targetWeightKg: 1.380 },
  { ageDays: 35, ageWeeks: 5,  targetWeightKg: 1.960 },
  { ageDays: 42, ageWeeks: 6,  targetWeightKg: 2.530 },
  { ageDays: 49, ageWeeks: 7,  targetWeightKg: 3.070 },
  { ageDays: 56, ageWeeks: 8,  targetWeightKg: 3.560 },
];

export const LOHMANN_TARGETS: GrowthCurvePoint[] = [
  { ageDays: 0,   ageWeeks: 0,  targetWeightKg: 0.038 },
  { ageDays: 7,   ageWeeks: 1,  targetWeightKg: 0.075 },
  { ageDays: 14,  ageWeeks: 2,  targetWeightKg: 0.130 },
  { ageDays: 21,  ageWeeks: 3,  targetWeightKg: 0.210 },
  { ageDays: 28,  ageWeeks: 4,  targetWeightKg: 0.320 },
  { ageDays: 35,  ageWeeks: 5,  targetWeightKg: 0.450 },
  { ageDays: 42,  ageWeeks: 6,  targetWeightKg: 0.590 },
  { ageDays: 56,  ageWeeks: 8,  targetWeightKg: 0.820 },
  { ageDays: 70,  ageWeeks: 10, targetWeightKg: 1.050 },
  { ageDays: 84,  ageWeeks: 12, targetWeightKg: 1.220 },
  { ageDays: 112, ageWeeks: 16, targetWeightKg: 1.450 },
  { ageDays: 140, ageWeeks: 20, targetWeightKg: 1.650 },
  { ageDays: 168, ageWeeks: 24, targetWeightKg: 1.820 },
  { ageDays: 196, ageWeeks: 28, targetWeightKg: 1.900 },
];

export const BREED_TARGETS: Record<string, GrowthCurvePoint[]> = {
  'Cobb 500': COBB500_TARGETS,
  'Ross 308': ROSS308_TARGETS,
  'Lohmann Brown': LOHMANN_TARGETS,
  'ISA Brown': LOHMANN_TARGETS,
};

// ─── Mock Weight Sampling Records ────────────────────────────────────────────
export const MOCK_WEIGHT_SAMPLES: WeightSample[] = [
  // Flock BR-2026-002 (Ross 308, 44 days)
  {
    id: 'ws-001',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    breed: 'Ross 308',
    birdType: 'Broiler',
    recordDate: '2026-08-22',
    ageDays: 7,
    ageWeeks: 1,
    numberSampled: 50,
    individualWeights: [],
    avgWeightKg: 0.182,
    targetWeightKg: 0.185,
    minWeightKg: 0.155,
    maxWeightKg: 0.210,
    stdDeviation: 0.014,
    uniformityPct: 88.0,
    avgDailyGainG: 20.0,
    weeklyGainG: 140.0,
    fcr: 0.92,
    feedConsumedKg: 16.6,
    cumulativeFeedKg: 16.6,
    recordedBy: 'Okello James',
    notes: 'Good start, uniform placement',
  },
  {
    id: 'ws-002',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    breed: 'Ross 308',
    birdType: 'Broiler',
    recordDate: '2026-07-29',
    ageDays: 14,
    ageWeeks: 2,
    numberSampled: 50,
    individualWeights: [],
    avgWeightKg: 0.448,
    targetWeightKg: 0.460,
    minWeightKg: 0.390,
    maxWeightKg: 0.510,
    stdDeviation: 0.031,
    uniformityPct: 86.0,
    avgDailyGainG: 38.0,
    weeklyGainG: 266.0,
    fcr: 1.18,
    feedConsumedKg: 52.8,
    cumulativeFeedKg: 69.4,
    recordedBy: 'Okello James',
    notes: 'Slightly below target — check feeder space',
  },
  {
    id: 'ws-003',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    breed: 'Ross 308',
    birdType: 'Broiler',
    recordDate: '2026-08-05',
    ageDays: 21,
    ageWeeks: 3,
    numberSampled: 60,
    individualWeights: [],
    avgWeightKg: 0.855,
    targetWeightKg: 0.870,
    minWeightKg: 0.740,
    maxWeightKg: 0.970,
    stdDeviation: 0.058,
    uniformityPct: 84.0,
    avgDailyGainG: 58.1,
    weeklyGainG: 407.0,
    fcr: 1.34,
    feedConsumedKg: 114.6,
    cumulativeFeedKg: 184.0,
    recordedBy: 'Nakato Sarah',
    notes: '',
  },
  {
    id: 'ws-004',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    breed: 'Ross 308',
    birdType: 'Broiler',
    recordDate: '2026-08-12',
    ageDays: 28,
    ageWeeks: 4,
    numberSampled: 60,
    individualWeights: [],
    avgWeightKg: 1.345,
    targetWeightKg: 1.380,
    minWeightKg: 1.180,
    maxWeightKg: 1.510,
    stdDeviation: 0.082,
    uniformityPct: 83.3,
    avgDailyGainG: 70.0,
    weeklyGainG: 490.0,
    fcr: 1.52,
    feedConsumedKg: 204.4,
    cumulativeFeedKg: 388.4,
    recordedBy: 'Okello James',
    notes: 'FCR improving after feed adjustment',
  },
  {
    id: 'ws-005',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    breed: 'Ross 308',
    birdType: 'Broiler',
    recordDate: '2026-08-19',
    ageDays: 35,
    ageWeeks: 5,
    numberSampled: 60,
    individualWeights: [],
    avgWeightKg: 1.910,
    targetWeightKg: 1.960,
    minWeightKg: 1.680,
    maxWeightKg: 2.140,
    stdDeviation: 0.115,
    uniformityPct: 81.7,
    avgDailyGainG: 80.7,
    weeklyGainG: 565.0,
    fcr: 1.68,
    feedConsumedKg: 321.0,
    cumulativeFeedKg: 709.4,
    recordedBy: 'Nakato Sarah',
    notes: '',
  },
  {
    id: 'ws-006',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    breed: 'Ross 308',
    birdType: 'Broiler',
    recordDate: '2026-08-26',
    ageDays: 42,
    ageWeeks: 6,
    numberSampled: 60,
    individualWeights: [],
    avgWeightKg: 2.480,
    targetWeightKg: 2.530,
    minWeightKg: 2.190,
    maxWeightKg: 2.760,
    stdDeviation: 0.142,
    uniformityPct: 80.0,
    avgDailyGainG: 81.4,
    weeklyGainG: 570.0,
    fcr: 1.88,
    feedConsumedKg: 466.2,
    cumulativeFeedKg: 1175.6,
    recordedBy: 'Okello James',
    notes: 'Approaching market weight',
  },
  {
    id: 'ws-007',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    breed: 'Ross 308',
    birdType: 'Broiler',
    recordDate: '2026-09-02',
    ageDays: 44,
    ageWeeks: 6,
    numberSampled: 60,
    individualWeights: [],
    avgWeightKg: 1.820,
    targetWeightKg: 1.960,
    minWeightKg: 1.600,
    maxWeightKg: 2.050,
    stdDeviation: 0.112,
    uniformityPct: 82.0,
    avgDailyGainG: 79.0,
    weeklyGainG: 553.0,
    fcr: 1.92,
    feedConsumedKg: 498.0,
    cumulativeFeedKg: 1673.6,
    recordedBy: 'Okello James',
    notes: 'Latest record — current age',
  },
  // Flock BR-2026-003 (Cobb 500, 25 days)
  {
    id: 'ws-008',
    flockId: 'flock-003',
    flockNumber: 'BR-2026-003',
    flockName: 'Batch B1 — Cobb 500',
    farm: 'Idudi Poultry Farm',
    house: 'House B1',
    breed: 'Cobb 500',
    birdType: 'Broiler',
    recordDate: '2026-08-17',
    ageDays: 7,
    ageWeeks: 1,
    numberSampled: 50,
    individualWeights: [],
    avgWeightKg: 0.175,
    targetWeightKg: 0.170,
    minWeightKg: 0.148,
    maxWeightKg: 0.202,
    stdDeviation: 0.013,
    uniformityPct: 90.0,
    avgDailyGainG: 19.0,
    weeklyGainG: 133.0,
    fcr: 0.88,
    feedConsumedKg: 15.4,
    cumulativeFeedKg: 15.4,
    recordedBy: 'Nakato Sarah',
    notes: 'Excellent start — above target',
  },
  {
    id: 'ws-009',
    flockId: 'flock-003',
    flockNumber: 'BR-2026-003',
    flockName: 'Batch B1 — Cobb 500',
    farm: 'Idudi Poultry Farm',
    house: 'House B1',
    breed: 'Cobb 500',
    birdType: 'Broiler',
    recordDate: '2026-08-24',
    ageDays: 14,
    ageWeeks: 2,
    numberSampled: 50,
    individualWeights: [],
    avgWeightKg: 0.445,
    targetWeightKg: 0.430,
    minWeightKg: 0.385,
    maxWeightKg: 0.505,
    stdDeviation: 0.030,
    uniformityPct: 88.0,
    avgDailyGainG: 38.6,
    weeklyGainG: 270.0,
    fcr: 1.14,
    feedConsumedKg: 50.7,
    cumulativeFeedKg: 66.1,
    recordedBy: 'Nakato Sarah',
    notes: '',
  },
  {
    id: 'ws-010',
    flockId: 'flock-003',
    flockNumber: 'BR-2026-003',
    flockName: 'Batch B1 — Cobb 500',
    farm: 'Idudi Poultry Farm',
    house: 'House B1',
    breed: 'Cobb 500',
    birdType: 'Broiler',
    recordDate: '2026-09-04',
    ageDays: 25,
    ageWeeks: 3,
    numberSampled: 60,
    individualWeights: [],
    avgWeightKg: 0.740,
    targetWeightKg: 0.820,
    minWeightKg: 0.640,
    maxWeightKg: 0.840,
    stdDeviation: 0.052,
    uniformityPct: 85.0,
    avgDailyGainG: 26.8,
    weeklyGainG: 295.0,
    fcr: 1.41,
    feedConsumedKg: 104.4,
    cumulativeFeedKg: 170.5,
    recordedBy: 'Okello James',
    notes: 'Slight dip — monitor closely',
  },
  // Flock LY-2026-001 (Lohmann Brown, 232 days)
  {
    id: 'ws-011',
    flockId: 'flock-004',
    flockNumber: 'LY-2026-001',
    flockName: 'Layers Batch L1 — Lohmann',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    breed: 'Lohmann Brown',
    birdType: 'Layer',
    recordDate: '2026-07-15',
    ageDays: 182,
    ageWeeks: 26,
    numberSampled: 40,
    individualWeights: [],
    avgWeightKg: 1.880,
    targetWeightKg: 1.900,
    minWeightKg: 1.720,
    maxWeightKg: 2.040,
    stdDeviation: 0.082,
    uniformityPct: 87.5,
    avgDailyGainG: 2.1,
    weeklyGainG: 14.7,
    fcr: 2.14,
    feedConsumedKg: 84.0,
    cumulativeFeedKg: 3840.0,
    recordedBy: 'Nakato Sarah',
    notes: 'Stable production weight',
  },
  {
    id: 'ws-012',
    flockId: 'flock-004',
    flockNumber: 'LY-2026-001',
    flockName: 'Layers Batch L1 — Lohmann',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    breed: 'Lohmann Brown',
    birdType: 'Layer',
    recordDate: '2026-09-04',
    ageDays: 232,
    ageWeeks: 33,
    numberSampled: 40,
    individualWeights: [],
    avgWeightKg: 1.920,
    targetWeightKg: 1.900,
    minWeightKg: 1.760,
    maxWeightKg: 2.080,
    stdDeviation: 0.080,
    uniformityPct: 90.0,
    avgDailyGainG: 0.8,
    weeklyGainG: 5.6,
    fcr: 2.18,
    feedConsumedKg: 88.0,
    cumulativeFeedKg: 4928.0,
    recordedBy: 'Okello James',
    notes: 'Slightly above target — excellent uniformity',
  },
];

// ─── Flock Growth Summaries ───────────────────────────────────────────────────
export const FLOCK_GROWTH_SUMMARIES: FlockGrowthSummary[] = [
  {
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    breed: 'Ross 308',
    birdType: 'Broiler',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    currentAgeDays: 44,
    currentAvgWeightKg: 1.820,
    targetWeightKg: 1.960,
    weightVariancePct: -7.14,
    latestUniformityPct: 82.0,
    latestFcr: 1.92,
    latestAdg: 79.0,
    totalSamples: 7,
    status: 'below_target',
  },
  {
    flockId: 'flock-003',
    flockNumber: 'BR-2026-003',
    flockName: 'Batch B1 — Cobb 500',
    breed: 'Cobb 500',
    birdType: 'Broiler',
    farm: 'Idudi Poultry Farm',
    house: 'House B1',
    currentAgeDays: 25,
    currentAvgWeightKg: 0.740,
    targetWeightKg: 0.820,
    weightVariancePct: -9.76,
    latestUniformityPct: 85.0,
    latestFcr: 1.41,
    latestAdg: 26.8,
    totalSamples: 3,
    status: 'below_target',
  },
  {
    flockId: 'flock-004',
    flockNumber: 'LY-2026-001',
    flockName: 'Layers Batch L1 — Lohmann',
    breed: 'Lohmann Brown',
    birdType: 'Layer',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    currentAgeDays: 232,
    currentAvgWeightKg: 1.920,
    targetWeightKg: 1.900,
    weightVariancePct: 1.05,
    latestUniformityPct: 90.0,
    latestFcr: 2.18,
    latestAdg: 0.8,
    totalSamples: 2,
    status: 'above_target',
  },
  {
    flockId: 'flock-008',
    flockNumber: 'BR-2026-004',
    flockName: 'Batch C1 — Arbor Acres',
    breed: 'Arbor Acres',
    birdType: 'Broiler',
    farm: 'Kampala Demo Farm',
    house: 'House C1',
    currentAgeDays: 20,
    currentAvgWeightKg: 0.480,
    targetWeightKg: 0.480,
    weightVariancePct: 0.0,
    latestUniformityPct: 91.0,
    latestFcr: 1.28,
    latestAdg: 22.0,
    totalSamples: 1,
    status: 'on_track',
  },
];

// ─── Growth Curve Data for Chart (flock-002 actual vs target) ────────────────
export function buildGrowthCurveData(
  flockId: string,
  targets: GrowthCurvePoint[]
): Array<{ ageDays: number; ageLabel: string; target: number; actual?: number }> {
  const flockSamples = MOCK_WEIGHT_SAMPLES.filter(s => s.flockId === flockId)
    .sort((a, b) => a.ageDays - b.ageDays);

  const actualMap = new Map<number, number>();
  flockSamples.forEach(s => actualMap.set(s.ageDays, s.avgWeightKg));

  // Merge target points with actual data
  const allDays = new Set<number>([
    ...targets.map(t => t.ageDays),
    ...flockSamples.map(s => s.ageDays),
  ]);

  const sorted = Array.from(allDays).sort((a, b) => a - b);

  return sorted.map(day => {
    const targetPoint = targets.find(t => t.ageDays === day);
    // Interpolate target if not exact match
    let targetWeight = targetPoint?.targetWeightKg;
    if (!targetWeight) {
      const before = [...targets].reverse().find(t => t.ageDays <= day);
      const after = targets.find(t => t.ageDays >= day);
      if (before && after && before !== after) {
        const ratio = (day - before.ageDays) / (after.ageDays - before.ageDays);
        targetWeight = before.targetWeightKg + ratio * (after.targetWeightKg - before.targetWeightKg);
      } else {
        targetWeight = before?.targetWeightKg ?? after?.targetWeightKg ?? 0;
      }
    }
    return {
      ageDays: day,
      ageLabel: `Day ${day}`,
      target: Math.round(targetWeight * 1000) / 1000,
      actual: actualMap.get(day),
    };
  });
}
